import { Bot, ClipboardPaste, FileText, GitCompare, Rocket, Search, Shield, Upload } from 'lucide-react';

import type { NovaSkill, SlashCommand } from '../queries/chats';

// Re-export SlashCommand type from queries for convenience
export type { SlashCommand } from '../queries/chats';

export interface PromptCommand {
  id: string;
  kind: 'slash-command' | 'skill';
  handle: string;
  description: string;
  category: string;
  title?: string;
  prompt?: string;
  exampleUsage?: string;
  instructionPlaceholder?: string;
  slashCommand?: SlashCommand;
  skill?: NovaSkill;
}

/**
 * Interface for grouped slash commands by category
 */
export interface SlashCommandGroup {
  category: string;
  commands: SlashCommand[];
}

export interface PromptCommandGroup {
  category: string;
  commands: PromptCommand[];
}

/**
 * Map slash command categories to Lucide icons
 */
export const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  GET: Search,
  FILL: FileText,
  REQUEST: Upload,
  INGEST: ClipboardPaste,
  CREATE: FileText,
  COMPARE: GitCompare,
  AUDIT: Shield,
  NEW: Rocket,
  SKILL: Bot,
};

/**
 * Map slash command categories to human-readable display names
 */
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  GET: 'Get',
  FILL: 'Fill',
  REQUEST: 'Request',
  INGEST: 'Ingest',
  CREATE: 'Create',
  COMPARE: 'Compare',
  AUDIT: 'Audit',
  NEW: "What's New",
  SKILL: 'Skills',
};

export function buildPromptCommandFromSkill(skill: NovaSkill): PromptCommand {
  return {
    id: `skill:${skill.id}`,
    kind: 'skill',
    handle: skill.id,
    title: skill.name,
    description: skill.description ?? '',
    category: 'SKILL',
    skill,
  };
}

export function buildPromptCommandFromSlashCommand(command: SlashCommand): PromptCommand {
  return {
    id: `slash:${command.uuid}`,
    kind: 'slash-command',
    handle: command.handle,
    description: command.description,
    category: command.category,
    prompt: command.prompt,
    exampleUsage: command.exampleUsage ?? undefined,
    instructionPlaceholder: command.instructionPlaceholder ?? undefined,
    slashCommand: command,
  };
}

export function buildPromptCommands(slashCommands: SlashCommand[], skills: NovaSkill[]): PromptCommand[] {
  const skillCommands = [...skills]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map(buildPromptCommandFromSkill);

  const slashPromptCommands = slashCommands.map(buildPromptCommandFromSlashCommand);

  return [...slashPromptCommands, ...skillCommands];
}

export function isSlashPromptCommand(
  command: PromptCommand,
): command is PromptCommand & { kind: 'slash-command'; slashCommand: SlashCommand } {
  return command.kind === 'slash-command' && !!command.slashCommand;
}
